/* WORTH AGAINST VALUE. Two columns, never one score.
 *
 * A gym owner asking "who is worth more than they pay" is asking about PEOPLE, and
 * the answer is a list of them. Blending what somebody pays with what they bring
 * into a single figure would be inventing a number (SITE-RULES 3), and it would
 * also destroy the only interesting thing on the screen: the members where the two
 * columns disagree.
 *
 * The member on the cheapest plan who brought three friends and is named by five
 * people is not a cheap member. The one on the most expensive plan whom nobody
 * names is not a safe one. Both are one glance apart here, and every figure on the
 * row opens the names behind it.
 *
 * OWNER ONLY, because it is the one seat that may lead with money at all.
 */
import { useState } from 'react';
import type { Member } from '@acrostic/model';
import { useAsync, useSource } from '../data/SourceProvider.js';
import type { Worth as WorthRow, WorthSort } from '../data/contract.js';
import { NoneRow } from '../components/Panel.js';
import { Modal } from '../components/Modal.js';
import { Avatar } from '../components/Avatar.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';

const SORTS: { key: WorthSort; label: string }[] = [
  { key: 'gap', label: 'Bring most for what they pay' },
  { key: 'brings', label: 'Brought the most' },
  { key: 'known', label: 'Named by the most people' },
  { key: 'pays', label: 'Pay the most' },
];

const SIZE = 25;

export function Worth() {
  const source = useSource();
  const [sort, setSort] = useState<WorthSort>('gap');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<{ title: string; people: Member[] } | null>(null);
  const { data, error } = useAsync(() => source.getWorth({ sort, page, size: SIZE }), [source, sort, page]);

  return (
    <>
      <section className="panel">
        <header>
          <h2 className="t-heading-h3">Worth and value</h2>
          <span className="eyebrow t-label-xsmall">
            What a member pays, beside what they bring
          </span>
          <span className="spacer" />
          <label className="t-caption muted">
            Sort{' '}
            <select className="t-label-small" value={sort}
              onChange={(e) => { setSort(e.target.value as WorthSort); setPage(1); }}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </header>


        {error && <NoneRow>Could not read the roster. {error.message}</NoneRow>}
        {!error && !data && <NoneRow>Reading…</NoneRow>}

        {data && (
          <table className="table worth">
            <thead>
              <tr className="t-label-small">
                <th>Member</th>
                <th className="right">Pays</th>
                <th className="right">With you</th>
                <th>Brought in</th>
                <th className="right">They pay</th>
                <th>Known by them</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((w) => (
                <Row key={w.member.id} w={w} onOpen={setOpen} />
              ))}
            </tbody>
          </table>
        )}

        {data && data.total > SIZE && (
          <div className="pager t-label-small">
            <button type="button" className="btn" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span className="muted">
              {(page - 1) * SIZE + 1}–{Math.min(page * SIZE, data.total)} of {fmt.num(data.total)}
            </span>
            <button type="button" className="btn" disabled={page * SIZE >= data.total}
              onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        )}
      </section>

      {open && (
        <Modal title={open.title} onClose={() => setOpen(null)}
          footer={<button type="button" className="btn t-label-medium" onClick={() => setOpen(null)}>Done</button>}>
          <div className="rows register">
            {open.people.map((m) => (
              <button key={m.id} type="button" className="row clickable"
                onClick={() => { setOpen(null); go('/members/' + m.id); }}>
                <Avatar name={m.name} photoUrl={m.photoUrl} size="md" />
                <div className="who">
                  <div className="n t-label-large">{m.name}</div>
                  <div className="s t-caption muted">
                    {[m.plan, m.priceCents == null ? null : fmt.money(m.priceCents) + '/mo']
                      .filter(Boolean).join(' · ') || 'No plan on record'}
                  </div>
                </div>
                <span className="t-label-medium muted">→</span>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}

function Row({ w, onOpen }: {
  w: WorthRow; onOpen: (o: { title: string; people: Member[] }) => void;
}) {
  const m = w.member;
  /* The row is highlighted when what they bring is worth more than what they pay.
     Not a badge and not a score: the two numbers are already on the row, and this
     only says which way round they are. */
  const bringsMore = w.broughtPaysCents > (w.paysCents ?? 0);
  return (
    <tr className={bringsMore ? 'brings-more' : undefined} style={{ cursor: 'default' }}>
      <td>
        <Avatar name={m.name} photoUrl={m.photoUrl} size="sm" />
        <button type="button" className="namelink t-label-large"
          onClick={() => go('/members/' + m.id)}>{m.name}</button>
      </td>
      <td className="right t-body-small">
        {w.paysCents == null ? <span className="muted">Not recorded</span> : fmt.money(w.paysCents) + '/mo'}
      </td>
      {/* The length of the relationship, which is not a value and is not treated as
          one. It is here because "brought nobody in two months" and "brought nobody
          in six years" are different facts. */}
      <td className="right t-body-small muted">
        {w.months < 1 ? 'new' : w.months < 12 ? `${w.months} mo` : `${Math.floor(w.months / 12)} yr`}
      </td>
      <td>
        {!w.brought.length ? <span className="muted t-body-small">—</span> : (
          <button type="button" className="linkish t-label-small"
            onClick={() => onOpen({ title: `${m.name} brought these people in`, people: w.brought })}>
            {w.brought.length === 1 ? w.brought[0]!.name : `${w.brought.length} people`}
          </button>
        )}
      </td>
      <td className="right t-body-small">
        {w.broughtPaysCents
          ? <strong>{fmt.money(w.broughtPaysCents)}/mo</strong>
          : <span className="muted">—</span>}
      </td>
      <td>
        {!w.anchorFor.length ? <span className="muted t-body-small">—</span> : (
          <button type="button" className="linkish t-label-small"
            onClick={() => onOpen({ title: `These members say they know ${m.firstName}`, people: w.anchorFor })}>
            {w.anchorFor.length === 1 ? w.anchorFor[0]!.name : `${w.anchorFor.length} members`}
          </button>
        )}
      </td>
    </tr>
  );
}
